/** Formation tab on the admin model detail.
 *
 * Controls a model's access to:
 *   - Tronc commun (the core course track)
 *   - Each complementary module (themes from track "modules-complementaires")
 *   - Sous gestion (unlocks the management section of his portal)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, CheckCircle } from "@phosphor-icons/react";
import { modelsAdminApi } from "../../../api/models";
import { coursesApi } from "../../../api/courses";

const LABEL =
  "block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-2";

export default function AdminModelFormation() {
  const { t } = useTranslation();
  const { modelId, model, reload } = useOutletContext();
  const [troncCommun, setTroncCommun] = useState(true);
  const [underMgmt, setUnderMgmt] = useState(false);
  const [allowed, setAllowed] = useState([]); // module ids
  const [modules, setModules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // Sync local state when model loads
  useEffect(() => {
    if (!model) return;
    setTroncCommun(model.access_tronc_commun !== false);
    setUnderMgmt(!!model.under_management);
    setAllowed(model.access_complementary_modules || []);
  }, [model]);

  // Load the list of complementary modules (themes from the "modules-complementaires" track)
  useEffect(() => {
    (async () => {
      try {
        const tracks = await coursesApi.tracks();
        const complementaryTrack = (tracks || []).find(
          (tr) => tr.slug === "modules-complementaires",
        );
        setModules(complementaryTrack?.themes || []);
      } catch {
        setModules([]);
      }
    })();
  }, []);

  const toggle = (id) =>
    setAllowed((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const allChecked = useMemo(
    () => modules.length > 0 && modules.every((m) => allowed.includes(m.id)),
    [modules, allowed],
  );

  const toggleAll = () =>
    setAllowed(allChecked ? [] : modules.map((m) => m.id));

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await modelsAdminApi.update(modelId, {
        access_tronc_commun: troncCommun,
        under_management: underMgmt,
        access_complementary_modules: allowed,
      });
      setSavedAt(new Date());
      reload?.();
    } finally {
      setSaving(false);
    }
  }, [modelId, troncCommun, underMgmt, allowed, reload]);

  if (!model)
    return <p className="text-sm text-slate-500">{t("common.loading")}</p>;

  return (
    <div
      data-testid="admin-model-formation-tab"
      className="space-y-8 max-w-3xl"
    >
      {/* Tronc commun */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <p className={LABEL}>{t("models.formation.section_main")}</p>
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            data-testid="formation-tronc-commun"
            className="mt-1 h-4 w-4 rounded border-slate-300"
            checked={troncCommun}
            onChange={(e) => setTroncCommun(e.target.checked)}
          />
          <div>
            <p className="text-sm font-semibold text-slate-900">
              {t("models.formation.tronc_commun")}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {t("models.formation.tronc_commun_help")}
            </p>
          </div>
        </label>
      </section>

      {/* Sous gestion */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <p className={LABEL}>{t("models.formation.section_management")}</p>
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            data-testid="formation-under-management"
            className="mt-1 h-4 w-4 rounded border-slate-300"
            checked={underMgmt}
            onChange={(e) => setUnderMgmt(e.target.checked)}
          />
          <div>
            <p className="text-sm font-semibold text-slate-900">
              {t("models.formation.under_management")}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {t("models.formation.under_management_help")}
            </p>
          </div>
        </label>
      </section>

      {/* Complementary modules */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <p className={LABEL + " mb-0"}>
            {t("models.formation.section_modules")}
          </p>
          {modules.length > 0 && (
            <button
              type="button"
              data-testid="formation-modules-toggle-all"
              onClick={toggleAll}
              className="text-xs font-medium text-slate-700 hover:text-slate-900 underline underline-offset-4 decoration-slate-300"
            >
              {allChecked
                ? t("models.formation.uncheck_all")
                : t("models.formation.check_all")}
            </button>
          )}
        </div>
        {modules.length === 0 ? (
          <p className="text-sm text-slate-500">
            {t("models.formation.modules_empty")}
          </p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {modules.map((m) => {
              const checked = allowed.includes(m.id);
              return (
                <li key={m.id}>
                  <label
                    className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                      checked
                        ? "border-emerald-200 bg-emerald-50/50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <input
                      type="checkbox"
                      data-testid={`formation-module-${m.id}`}
                      className="h-4 w-4 rounded border-slate-300"
                      checked={checked}
                      onChange={() => toggle(m.id)}
                    />
                    <span className="flex-1 text-sm font-medium text-slate-900">
                      {m.title}
                    </span>
                    {checked ? (
                      <CheckCircle
                        size={15}
                        weight="fill"
                        className="text-emerald-600"
                      />
                    ) : (
                      <Lock size={14} weight="regular" className="text-slate-300" />
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <div className="flex justify-end items-center gap-3">
        {savedAt && (
          <span className="text-xs text-emerald-600">
            {t("admin.courses.saved_at", {
              time: savedAt.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            })}
          </span>
        )}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="formation-save"
          className="rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          {saving ? t("admin.courses.saving") : t("common.save")}
        </button>
      </div>
    </div>
  );
}
